import { useCallback, useState } from "react";

import * as api from "../api/client";
import { SortableTh } from "../components/SortableTh";
import { useApi } from "../hooks/useApi";
import { useSortableRows } from "../hooks/useSortableRows";
import type { UserDetails, UserListItem, UserSearchResult } from "../types/api";

type UserSortKey =
  | "uid"
  | "email"
  | "tier"
  | "registeredAt"
  | "billingInterval"
  | "generationCount";
const USER_ACCESSORS: Record<UserSortKey, (u: UserListItem) => string | number | null> = {
  uid: (u) => u.uid,
  email: (u) => u.email,
  tier: (u) => u.tier,
  registeredAt: (u) => u.registeredAt,
  billingInterval: (u) => u.billingInterval,
  generationCount: (u) => u.generationCount,
};

export function UsersPage() {
  // -------------------------------------------------------------------------
  // Users list — GET /v1/admin/users is cursor-paginated at the wire level,
  // but a cursor-paginated table cannot support honest column sorting (a
  // client-side sort would silently apply to one page only). At this
  // product's current scale (tens of users) `listAllUsers` pages through the
  // whole collection internally and this page sorts the full, real set — no
  // fake Prev/Next controls (PK 2026-07-25 directive: sort must hit the
  // source, or pagination must disappear).
  // -------------------------------------------------------------------------
  const usersList = useApi(() => api.listAllUsers(), []);
  // Default: most-recently-registered user first — useful without clicking.
  const userSort = useSortableRows(usersList.data?.users, USER_ACCESSORS, {
    key: "registeredAt",
    direction: "desc",
  });

  // -------------------------------------------------------------------------
  // User search + detail
  // -------------------------------------------------------------------------
  const [searchEmail, setSearchEmail] = useState("");
  const [searchResults, setSearchResults] = useState<UserSearchResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserDetails | null>(null);
  const [giftAmount, setGiftAmount] = useState(10);
  const [giftType, setGiftType] = useState<"purchased">("purchased");
  const [giftStatus, setGiftStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleSearch = useCallback(async () => {
    if (!searchEmail.trim()) return;
    setSearching(true);
    setError(null);
    try {
      const users = await api.searchUsers(searchEmail);
      setSearchResults(users);
      setSelectedUser(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Search failed");
    } finally {
      setSearching(false);
    }
  }, [searchEmail]);

  const handleSelectUser = useCallback(async (uid: string) => {
    setError(null);
    try {
      const details = await api.getUserDetails(uid);
      setSelectedUser(details);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load user");
    }
  }, []);

  const handleGift = useCallback(async () => {
    if (!selectedUser) return;
    setGiftStatus(null);
    setError(null);
    try {
      await api.giftGenerations(selectedUser.uid, { amount: giftAmount, type: giftType });
      setGiftStatus(`Gifted ${giftAmount} ${giftType} generations`);
      const details = await api.getUserDetails(selectedUser.uid);
      setSelectedUser(details);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Gift failed");
    }
  }, [selectedUser, giftAmount, giftType]);

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------
  return (
    <div>
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-slate-900">Users</h2>
        <p className="text-sm text-slate-500 mt-1">Browse all users or search by email</p>
      </div>

      {/* ------------------------------------------------------------------ */}
      {/* All-users table                                                     */}
      {/* ------------------------------------------------------------------ */}
      <section className="mb-10">
        <h3 className="text-lg font-semibold text-slate-800 mb-3">
          All Users{usersList.data ? ` (${usersList.data.users.length})` : ""}
        </h3>

        {usersList.loading && (
          <div className="text-sm text-slate-500 py-6 text-center">Loading users…</div>
        )}

        {!usersList.loading && usersList.error && (
          <div
            className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-4 mb-4 text-sm"
            role="alert"
          >
            {usersList.error}
          </div>
        )}

        {usersList.data?.truncated && (
          <div
            className="flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4"
            role="alert"
          >
            <span className="text-2xl">⚠️</span>
            <div>
              <div className="text-amber-800 font-semibold">User list truncated</div>
              <div className="text-amber-700 text-sm">
                The users collection hit the fetch page cap — the table below is a partial view, not
                the full set.
              </div>
            </div>
          </div>
        )}

        {!usersList.loading && !usersList.error && (
          <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
            {/* V2M-DEV-610: 6 columns don't fit a phone screen — scroll
                horizontally instead of clipping/hiding columns, with UID
                (below) pinned so the row stays identifiable mid-scroll. */}
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <SortableTh
                      label="UID"
                      sortKey="uid"
                      sort={userSort.sort}
                      onSort={userSort.toggleSort}
                      sticky
                    />
                    <SortableTh
                      label="Email"
                      sortKey="email"
                      sort={userSort.sort}
                      onSort={userSort.toggleSort}
                    />
                    <SortableTh
                      label="Tier"
                      sortKey="tier"
                      sort={userSort.sort}
                      onSort={userSort.toggleSort}
                    />
                    <SortableTh
                      label="Registered"
                      sortKey="registeredAt"
                      sort={userSort.sort}
                      onSort={userSort.toggleSort}
                    />
                    <SortableTh
                      label="Billing Cycle"
                      sortKey="billingInterval"
                      sort={userSort.sort}
                      onSort={userSort.toggleSort}
                    />
                    <SortableTh
                      label="Emails Generated"
                      sortKey="generationCount"
                      sort={userSort.sort}
                      onSort={userSort.toggleSort}
                      align="right"
                    />
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {userSort.sorted.length === 0 ? (
                    <tr>
                      <td
                        colSpan={6}
                        className="px-4 py-8 text-sm text-slate-400 text-center"
                        data-testid="user-list-empty"
                      >
                        No users found
                      </td>
                    </tr>
                  ) : (
                    userSort.sorted.map((u) => (
                      <tr key={u.uid} className="group hover:bg-slate-50 transition-colors">
                        <td className="sticky left-0 z-10 bg-white group-hover:bg-slate-50 px-4 py-3 text-xs font-mono text-slate-500 max-w-[160px] truncate">
                          {u.uid}
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-900">{u.email}</td>
                        <td className="px-4 py-3 text-sm">
                          <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-700">
                            {u.tier}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-900">
                          {u.registeredAt ? new Date(u.registeredAt).toLocaleDateString() : "—"}
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-600">
                          {u.billingInterval ? `${u.billingInterval}ly` : "—"}
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-900 text-right font-mono">
                          {u.generationCount.toLocaleString()}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </section>

      {/* ------------------------------------------------------------------ */}
      {/* Search                                                              */}
      {/* ------------------------------------------------------------------ */}
      <section>
        <h3 className="text-lg font-semibold text-slate-800 mb-3">Search by Email</h3>

        <div className="flex gap-3 mb-6">
          <input
            aria-label="Search by email"
            value={searchEmail}
            onChange={(e) => setSearchEmail(e.target.value)}
            placeholder="Search by email..."
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            data-testid="user-search-input"
            className="flex-1 px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
          />
          <button
            onClick={handleSearch}
            disabled={searching}
            type="button"
            className="bg-blue-600 hover:bg-blue-700 text-white font-medium px-6 py-2 rounded-lg transition-colors disabled:bg-slate-300 disabled:cursor-not-allowed"
          >
            {searching ? "Searching..." : "Search"}
          </button>
        </div>

        {error && (
          <div
            className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-4 mb-6"
            role="alert"
          >
            {error}
          </div>
        )}

        {searchResults.length > 0 && !selectedUser && (
          <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="sticky left-0 z-10 bg-slate-50 text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Email
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Name
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Tier
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {searchResults.map((u) => (
                    <tr key={u.uid} className="group hover:bg-slate-50 transition-colors">
                      <td className="sticky left-0 z-10 bg-white group-hover:bg-slate-50 px-4 py-3 text-sm text-slate-900">
                        {u.email}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-900">{u.displayName}</td>
                      <td className="px-4 py-3 text-sm">
                        <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-700">
                          {u.tier}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <button
                          onClick={() => handleSelectUser(u.uid)}
                          type="button"
                          className="text-blue-600 hover:text-blue-800 font-medium transition-colors"
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {selectedUser && (
          <div>
            <button
              onClick={() => setSelectedUser(null)}
              type="button"
              className="mb-6 text-blue-600 hover:text-blue-800 font-medium transition-colors flex items-center gap-1"
            >
              ← Back to results
            </button>

            <div className="bg-white rounded-lg border border-slate-200 p-6 mb-6">
              <h3 className="text-xl font-bold text-slate-900">{selectedUser.displayName}</h3>
              <p className="text-sm text-slate-500">{selectedUser.email}</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div className="bg-white rounded-lg border border-slate-200 p-5">
                <h4 className="text-sm font-medium text-slate-500 uppercase tracking-wider mb-3">
                  Subscription
                </h4>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-sm text-slate-600">Tier</span>
                    <span className="text-sm font-medium text-slate-900">
                      {selectedUser.subscription.tier}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-slate-600">Status</span>
                    <span className="text-sm font-medium text-slate-900">
                      {selectedUser.subscription.status}
                    </span>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg border border-slate-200 p-5">
                <h4 className="text-sm font-medium text-slate-500 uppercase tracking-wider mb-3">
                  Quota
                </h4>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-sm text-slate-600">Used</span>
                    <span className="text-sm font-medium text-slate-900">
                      {selectedUser.quota.used}/{selectedUser.quota.limit}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-slate-600">Period End</span>
                    <span className="text-sm font-medium text-slate-900">
                      {new Date(selectedUser.quota.periodEnd).toLocaleDateString()}
                    </span>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg border border-slate-200 p-5">
                <h4 className="text-sm font-medium text-slate-500 uppercase tracking-wider mb-3">
                  Feedback Stats
                </h4>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-sm text-slate-600">Total Ratings</span>
                    <span className="text-sm font-medium text-slate-900">
                      {selectedUser.feedbackStats.totalRatings}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-slate-600">Bonus Generations</span>
                    <span className="text-sm font-medium text-slate-900">
                      {selectedUser.feedbackStats.bonusGenerations}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-slate-600">Pending</span>
                    <span className="text-sm font-medium text-slate-900">
                      {selectedUser.feedbackStats.pendingBonusRatings}/5
                    </span>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg border border-slate-200 p-5">
                <h4 className="text-sm font-medium text-slate-500 uppercase tracking-wider mb-3">
                  Purchased
                </h4>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-sm text-slate-600">Remaining</span>
                    <span className="text-sm font-medium text-slate-900">
                      {selectedUser.purchasedGenerations}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg border border-slate-200 p-6">
              <h4 className="text-lg font-semibold text-slate-900 mb-4">Gift Generations</h4>
              <div className="flex flex-wrap gap-3 items-center">
                <input
                  aria-label="Gift amount"
                  type="number"
                  value={giftAmount}
                  onChange={(e) => setGiftAmount(Number(e.target.value))}
                  min={1}
                  data-testid="gift-amount"
                  className="w-24 px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                />
                <select
                  value={giftType}
                  onChange={(e) => setGiftType(e.target.value as "purchased")}
                  data-testid="gift-type"
                  className="px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                >
                  <option value="purchased">Purchased</option>
                </select>
                <button
                  onClick={handleGift}
                  type="button"
                  className="bg-green-600 hover:bg-green-700 text-white font-medium px-4 py-2 rounded-lg transition-colors"
                >
                  Gift
                </button>
              </div>
              {giftStatus && (
                <div className="mt-4 bg-green-50 border border-green-200 text-green-700 rounded-lg p-3 text-sm">
                  {giftStatus}
                </div>
              )}
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
